const asNonNegativeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
};

const clampScore = (value) => Math.min(100, Math.max(0, Math.round(value)));

const getControlEvidence = (frameworks = []) => frameworks.reduce((totals, framework) => {
  (framework.controls || []).forEach((control) => {
    const evaluated = asNonNegativeNumber(control.total_scanned);
    const violations = Math.min(evaluated, asNonNegativeNumber(control.violations));
    totals.evaluated += evaluated;
    totals.violations += violations;
  });
  return totals;
}, { evaluated: 0, violations: 0 });

/**
 * Score compliance from evaluated control checks, so larger frameworks and
 * controls with more observed violations have proportionate influence.
 * Framework-level scores are used only when control evidence is unavailable.
 */
export const calculateEvidenceWeightedScore = (frameworks = []) => {
  const evidence = getControlEvidence(frameworks);
  if (evidence.evaluated > 0) {
    return clampScore(((evidence.evaluated - evidence.violations) / evidence.evaluated) * 100);
  }

  const fallback = frameworks.reduce((totals, framework) => {
    const controlCount = asNonNegativeNumber(
      framework.totalControls ?? framework.total_controls ?? framework.controls?.length,
    );
    const score = Number(framework.score);
    if (controlCount > 0 && Number.isFinite(score)) {
      totals.weightedScore += Math.min(100, Math.max(0, score)) * controlCount;
      totals.controlCount += controlCount;
    }
    return totals;
  }, { weightedScore: 0, controlCount: 0 });

  return fallback.controlCount > 0
    ? clampScore(fallback.weightedScore / fallback.controlCount)
    : null;
};

export const getComplianceStatus = (score) => {
  if (score == null) return 'not-configured';
  if (score >= 90) return 'compliant';
  if (score >= 70) return 'needs-attention';
  return 'non-compliant';
};

export const summarizeFrameworkGroup = (frameworks = []) => {
  const passingControls = frameworks.reduce(
    (sum, framework) => sum + asNonNegativeNumber(
      framework.passingControls ?? framework.passing_controls,
    ),
    0,
  );
  const totalControls = frameworks.reduce(
    (sum, framework) => sum + asNonNegativeNumber(
      framework.totalControls ?? framework.total_controls,
    ),
    0,
  );
  // Match the headline to the pooled Controls Met tile. Individual framework
  // scores may still use evidence weighting, but the aggregate definition is
  // deliberately passing controls divided by evaluated controls.
  const score = totalControls > 0
    ? clampScore((passingControls / totalControls) * 100)
    : null;
  return {
    overall_score: score,
    overall_status: getComplianceStatus(score),
    framework_count: frameworks.length,
    passing_controls: passingControls,
    total_controls: totalControls,
    total_violations: frameworks.reduce(
      (sum, framework) => sum + asNonNegativeNumber(framework.violations),
      0,
    ),
  };
};

const frameworkControls = (framework) => ({
  passing: asNonNegativeNumber(framework.passingControls ?? framework.passing_controls),
  total: asNonNegativeNumber(framework.totalControls ?? framework.total_controls),
});

/**
 * The compliance figures the Saf3AI Agent may quote, built from the same
 * stats and framework rows the page renders (GE-116 / GE-124): one compliance
 * model for page and agent, including per-framework results.
 */
export const buildCompliancePageSummary = (stats = {}, frameworks = []) => {
  const rows = frameworks
    .filter((framework) => framework.name || framework.fullName)
    .map((framework) => {
      const { passing, total } = frameworkControls(framework);
      return {
        name: framework.name || framework.fullName,
        custom: Boolean(framework.isCustom ?? framework.is_custom),
        passing,
        total,
        violations: asNonNegativeNumber(framework.violations),
      };
    });
  const describe = (row) => `${row.name}: ${row.passing}/${row.total} controls passing, `
    + `${row.violations} violation records${row.custom ? ' (custom rule)' : ''}`;
  const weakest = rows
    .filter((row) => !row.custom && row.total > 0)
    .sort((a, b) => (a.passing / a.total) - (b.passing / b.total) || b.violations - a.violations)[0];

  return {
    compliance_score: stats.overall_score,
    controls_met: stats.passing_controls,
    total_controls: stats.total_controls,
    rule_violations: stats.total_violations,
    interactions_evaluated: stats.total_scanned,
    tracked_frameworks: rows.map((row) => row.name),
    framework_results: rows.map(describe).join('; '),
    weakest_certified_framework: weakest ? describe(weakest) : undefined,
  };
};

export const summarizeComplianceFrameworks = (frameworks = []) => ({
  certified: summarizeFrameworkGroup(frameworks.filter(
    (framework) => !(framework.isCustom ?? framework.is_custom ?? false),
  )),
  custom: summarizeFrameworkGroup(frameworks.filter(
    (framework) => framework.isCustom ?? framework.is_custom ?? false,
  )),
});
